// LeadCaptureMethod.tsx
import React, { useState, useEffect } from "react";
import { GetLeadCaptureMethod } from "../Services/APIService";

interface LeadCaptureItem {
  logId: number;
  leadId: number;
  tenantId: number;
  incomingSourceName?: string | null;
  incomingSubSourceName?: string | null;
  campaignName?: string | null;
  interestedProjectName?: string | null;
  logNotes?: string | null;
  createdOn: string;
  captureType?: string | null;
}

interface LeadCaptureMethodProps {
  leadId: number;
  tenantId?: number; // optional, you can pass tenant from parent or default in service
}

const LeadCaptureMethod: React.FC<LeadCaptureMethodProps> = ({
  leadId,
  tenantId = Number(localStorage.getItem("tenantId") || 0),
}) => {
  const [items, setItems] = useState<LeadCaptureItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await GetLeadCaptureMethod(leadId, tenantId);
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching lead capture method:", err);
        setError(
          "Failed to load lead capture methods. Please try again later."
        );
      } finally {
        setIsLoading(false);
      }
    };
    if (leadId) fetchData();
  }, [leadId, tenantId]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+ [AP]M)/, "$1-$2-$3, $4");
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="card-body text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading capture methods...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="card-body text-center">
          <div className="mb-3">
            <i
              className="bi bi-exclamation-triangle"
              style={{ fontSize: "3rem", color: "#dc3545" }}
            />
          </div>
          <h5 className="text-danger">{error}</h5>
        </div>
      );
    }

    if (!items || items.length === 0) {
      return (
        <div className="card-body text-center">
          <div className="mb-3">
            <i
              className="bi bi-inbox"
              style={{ fontSize: "3rem", color: "#6c757d" }}
            />
          </div>
          <h5 className="text-muted">No records to show</h5>
          <p className="text-muted">No capture methods found.</p>
        </div>
      );
    }

    return (
      <div className="cardtask position-relative">
        {items.map((it, idx) => (
          <div
            key={it.logId ?? idx}
            className="d-flex position-relative LeadCaptureMethod task-item"
          >
            <div className="flex-grow-1">
              <div className="card border mb-0 logData-card">
                {/* Card header: Capture Type + Received On */}
                <div className="card-header LCM-card-header d-flex justify-content-between align-items-center">
                  <div>
                    <span className="task-notes">
                      Capture Type: {it.captureType ?? "Unknown"}
                    </span>
                  </div>
                  <div className="text-end">
                    <small className="text-muted">Received On</small>
                    <div className="font-sizes">
                      {formatDateTime(it.createdOn)}
                    </div>
                  </div>
                </div>

                {/* Card body: kept exactly as before */}
                <div className="card-body p-3 position-relative">
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      {/* Received on + campaign + source/subsource/project shown together */}
                      <div className="row mt-2">
                        <div className="col-md-3 mb-3">
                          <div className="form-label fw-bold">Campaign</div>
                          <div className="mt-1 font-sizes lcm-field">
                            <span
                              className="lcm-ellipsis"
                              title={it.campaignName ?? ""}
                            >
                              {it.campaignName ?? "-"}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3 mb-3">
                          <div className="form-label fw-bold">Source</div>
                          <div className="mt-1 font-sizes lcm-field">
                            <span
                              className="lcm-ellipsis"
                              title={it.incomingSourceName ?? ""}
                            >
                              {it.incomingSourceName ?? "-"}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3 mb-3">
                          <div className="form-label fw-bold">Sub Source</div>
                          <div className="mt-1 font-sizes lcm-field">
                            <span
                              className="lcm-ellipsis"
                              title={it.incomingSubSourceName ?? ""}
                            >
                              {it.incomingSubSourceName ?? "-"}
                            </span>
                          </div>
                        </div>

                        <div className="col-md-3 mb-3">
                          <div className="form-label fw-bold">Project</div>
                          <div className="mt-1 font-sizes lcm-field">
                            <span
                              className="lcm-ellipsis"
                              title={it.interestedProjectName ?? ""}
                            >
                              {it.interestedProjectName ?? "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Optional old block left commented */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Lead Capture Method</h5>

        {/* Count badge replacing toggle */}
        <div>
          <span className="badge bg-primary">{items ? items.length : 0}</span>
        </div>
      </div>

      {/* Body always visible (no hide/show) */}
      <div id="lead-capture-body">{renderBody()}</div>
    </div>
  );
};

export default LeadCaptureMethod;
